import { Component, Inject, OnInit, ViewEncapsulation } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef, MatSnackBar } from "@angular/material";
import { FormBuilder, FormGroup } from "@angular/forms";
import { registrationFormConfig } from "./registration-form.config";
import { RegistrationFormItem, RegistrationFormOption } from "../../../types/registration-form.models";
import { GoogleTranslationService } from "../../../services/google-translation.service";
import { LanguageService } from "../../../services/language.service";

@Component({
  selector: "app-registration-form-dialog",
  templateUrl: "./registration-form-dialog.component.html",
  styleUrls: ["./registration-form-dialog.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class RegistrationFormDialogComponent implements OnInit {
  constructor(
    public dialogRef: MatDialogRef<RegistrationFormDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private data: any,
    private formBuilder: FormBuilder,
    private translationService: GoogleTranslationService,
    private snackBar: MatSnackBar,
    private languageService: LanguageService
  ) {}

  form: FormGroup;
  registrationFormConfig = registrationFormConfig;
  items: RegistrationFormItem[] = [];
  nonStaticItems: RegistrationFormItem[] = [];
  languages: string[] = ["en", "es", "fr"];
  languageDisplay: { name: string; code: string }[] = [];
  selectedLang: { [key: string]: string } = {};
  optionErrors: { [key: string]: string } = {};
  manualEditVisible: { [key: string]: boolean } = {};
  originalRegistrationForm: RegistrationFormItem[] = [];
  // Fields present in the incoming registrationForm that the dialog's built-in
  // config (registration-form.config.ts) does not model. The dialog has no UI
  // for these, so they are never edited here -- only carried through save()
  // untouched so they are not silently dropped (FEEDBACK p13).
  private unknownItems: any[] = [];

  ngOnInit() {
    let defaultConfig: any = this.registrationFormConfig || {};
    let defaultList: RegistrationFormItem[] = defaultConfig.registrationForm || [];
    let incomingConfig: any = (this.data && this.data.registrationFormConfig) || {};
    let incomingList: RegistrationFormItem[] = incomingConfig.registrationForm || [];
    if (!Array.isArray(incomingList)) incomingList = [];
    const incomingByKey: { [k: string]: RegistrationFormItem } = {};
    incomingList.forEach((i) => (incomingByKey[i.key] = i));

    // Capture any incoming fields the dialog's built-in config does not know
    // about (i.e. not one of the keys in registrationFormConfig). Without this,
    // the map() below -- which walks defaultList, not incomingList -- silently
    // drops them, and they never reappear in items/nonStaticItems or in the
    // save() result.
    const knownKeys = new Set(defaultList.map((cfg) => cfg.key));
    this.unknownItems = incomingList
      .filter((i) => i && !knownKeys.has(i.key))
      .map((i) => JSON.parse(JSON.stringify(i)));

    const merged: RegistrationFormItem[] = defaultList.map((cfg) => {
      const inc = incomingByKey[cfg.key];
      const base = JSON.parse(JSON.stringify(cfg));
      if (inc) {
        base.enabled = inc.enabled !== undefined ? inc.enabled : base.enabled;
        base.required = inc.required !== undefined ? inc.required : base.required;
        base.label = inc.label !== undefined ? inc.label : base.label;
        if (inc.options !== undefined) base.options = inc.options;
      } else {
        base.enabled = false;
      }
      return base;
    });
    let registrationForm: RegistrationFormItem[] = merged;

    this.items = registrationForm.filter((i) => i.static === true);
    // 4.2 (gap-analysis 05, root-cause 2): static entries historically carry
    // PLAIN-STRING labels while the entire editing machinery (getLabel,
    // setLabel, setLanguage, translateAllForItem) assumes a {lang: string}
    // map — which is why their titles were uneditable. Normalize on read so
    // both the built-in config defaults and games saved with the old string
    // shape keep working; the mobile consumer (RegistrationFormV1's
    // getFieldLabel) already accepts either shape.
    this.items.forEach((item) => {
      if (typeof item.label === 'string') {
        item.label = { en: item.label } as any;
      }
    });
    this.originalRegistrationForm = (this.registrationFormConfig.registrationForm || []).map(i => JSON.parse(JSON.stringify(i)));
    
    this.nonStaticItems = registrationForm
      .filter((i) => i.static !== true)
      .map((i) => {
        // Deep copy to avoid mutating original reference immediately
        const item = JSON.parse(JSON.stringify(i));
        item.label = item.label || {};
        
        // Internal structured state for dynamic options
        item.dynamicOptions = item.dynamicOptions || [];
        
        // If config has legacy 'options' map and no 'dynamicOptions' structured array, convert it
        // Check for mixed static+dynamic items (dropdowns with defaults)
        const startIndex = (item.type === 'dropdown' && item.staticBaseOptions && Array.isArray(item.staticBaseOptions)) 
            ? item.staticBaseOptions.length 
            : 0;

        if (!item.dynamicOptions.length && item.options && !Array.isArray(item.options)) {
            const opts = item.options as { [lang: string]: string[] };
            const enList = opts['en'] || [];
            
            // 1. Recover Static Translations (first N items)
            if (startIndex > 0) {
                 item.staticOptionsTranslations = item.staticOptionsTranslations || {};
                 this.languages.forEach(lang => {
                     // For English, the translation is the key itself, but we store it anyway if needed or skip
                     // Actually staticOptionsTranslations is usually for foreign langs.
                     if (!item.staticOptionsTranslations[lang]) item.staticOptionsTranslations[lang] = {};
                     
                     const list = opts[lang] || [];
                     for (let i = 0; i < startIndex; i++) {
                         const baseOpt = item.staticBaseOptions[i];
                         if (list[i] !== undefined) {
                             item.staticOptionsTranslations[lang][baseOpt] = list[i];
                         }
                     }
                 });
            }

            // 2. Recover Dynamic Options (remaining items)
            const dynamicEnList = enList.slice(startIndex);
            item.dynamicOptions = dynamicEnList.map((val, idx) => {
                const realIndex = startIndex + idx;
                const translations: any = {};
                this.languages.forEach(l => {
                    translations[l] = (opts[l] && opts[l][realIndex]) || "";
                });
                return { translations, enabled: true };
            });
        }
        
        // Initialize if empty (e.g. fresh config)
        if (!item.dynamicOptions) {
            item.dynamicOptions = [];
        }

        item.staticOptionsTranslations = item.staticOptionsTranslations || {};
        
        // Ensure all language keys exist for safer access
        this.languages.forEach(lang => {
          item.label[lang] = item.label[lang] || "";
          item.staticOptionsTranslations[lang] = item.staticOptionsTranslations[lang] || {};
        });

        // Ensure staticBaseOptions are present, default to empty array instead of dummy data
        item.staticBaseOptions = Array.isArray(item.staticBaseOptions) 
          ? item.staticBaseOptions 
          : [];
        return item;
      });

    // Initialize selected language early
    this.nonStaticItems.forEach((item) => {
      this.selectedLang[item.key] = "en";
    });

    // Create form ASAP to satisfy template bindings
    this.createForm();

    // Seed default for custom_dropdown_1 if empty AND if we didn't just load valid data
    this.nonStaticItems.forEach((item) => {
      if (item.key !== "custom_dropdown_1") return;
      
      // If we entered the loop above and parsed dynamicOptions/staticOptions, we likely have data.
      // But we check strictly if "en" has data.
      const labels = (item.label as { [k: string]: string }) || {};
      // If we managed to restore options, we don't need defaults.
      // We check if staticOptionsTranslations['en'] or dynamicOptions has content?
      // Actually simpler: just check if options map had content BEFORE we split it? 
      // Existing check: hasAnyEnLabel || hasAnyEnOptions
      const optionsMap = (item.options as { [lang: string]: string[] }) || {};
      const hasAnyEnLabel = !!(labels && labels["en"] && labels["en"].trim());
      const hasAnyEnOptions = !!(optionsMap["en"] && optionsMap["en"].length);
      
      if (hasAnyEnLabel || hasAnyEnOptions) return;

      labels["en"] = "What kind of pro are you?";
      labels["es"] = "¿Qué tipo de profesional eres?";
      labels["fr"] = "Quel type de professionnel êtes-vous ?";
      const enDefaults = [
        "Installer",
        "Remodeler",
        "Custom Home Builder",
        "Tract Home or Large Project Builder",
        "Design and Architecture",
        "Realtor/Investor/Developer",
        "Property Manager",
        "Restoration",
        "Outdoor Living",
        "Other Professional",
        "Not a PRO",
      ];
      const esDefaults = [
        "Instalador",
        "Remodelador",
        "Constructor de viviendas personalizadas",
        "Constructor de viviendas en serie o proyectos grandes",
        "Diseño y arquitectura",
        "Agente inmobiliario/Inversor/Desarrollador",
        "Administrador de propiedades",
        "Restauración",
        "Vida al aire libre",
        "Otro profesional",
        "No soy profesional",
      ];
      const frDefaults = [
        "Installateur",
        "Rénovateur",
        "Constructeur de maisons personnalisées",
        "Constructeur de lotissements ou grands projets",
        "Design et architecture",
        "Agent immobilier/Investisseur/Promoteur",
        "Gestionnaire immobilier",
        "Restauration",
        "Vie en plein air",
        "Autre professionnel",
        "Pas un professionnel",
      ];
      
      // We must set this directly into the parsed structure now, because 'options' map is ephemeral until save
      // 1. Static Options Translations
      item.staticBaseOptions = []; // Reset just in case? No, custom_dropdown_1 shouldn't have staticBaseOptions normally?
      // Wait, custom_dropdown_1 DOES NOT have staticBaseOptions in default config usually.
      // If it doesn't, startIndex is 0. So everything is dynamic.
      // So we just populate dynamicOptions.
      
      item.dynamicOptions = enDefaults.map((val, idx) => {
          return {
              translations: {
                  en: val,
                  es: esDefaults[idx] || "",
                  fr: frDefaults[idx] || ""
              },
              enabled: true
          };
      });
      // We don't touch optionsMap anymore as it's not used by UI
    });
    
    this.dialogRef.updateSize("850px", "auto");
    this.loadLanguages();
  }
  
  trackOption(index: number, _value: any) {
    return index;
  }

  close(response?: any) {
    this.dialogRef.close(response || null);
  }

  private async loadLanguages() {
    try {
      const list = await this.languageService.getLanguages();
      const codes = (list || []).map(l => (l && l.code) || '').filter(Boolean);
      this.languageDisplay = (list || []).map(l => ({ name: l.name, code: l.code }));
      if (!codes || !codes.length) return;
      this.languages = codes;
      
      // When languages load, ensure data structures exist
      this.nonStaticItems.forEach((item) => {
        this.languages.forEach((lang) => {
          item.label = item.label || {};
          (item.label as any)[lang] = (item.label as any)[lang] || "";
          item.staticOptionsTranslations = item.staticOptionsTranslations || {};
          item.staticOptionsTranslations[lang] = item.staticOptionsTranslations[lang] || {};
          
          // Backfill dynamic options keys
          if (item.dynamicOptions) {
             item.dynamicOptions.forEach(opt => {
                 if (!opt.translations[lang]) opt.translations[lang] = "";
             });
          }
        });
      });
    } catch (_) {}
  }
  private createForm() {
    const controlsConfig: any = {};
    
    // Setup controls for static items
    this.items.forEach((item) => {
      controlsConfig[`enabled_${item.key}`] = [item.enabled === true];
      controlsConfig[`required_${item.key}`] = [item.required === true];
      
      if (item.type === 'radio' && Array.isArray(item.options)) {
        (item.options as RegistrationFormOption[]).forEach((opt) => {
          controlsConfig[`enabled_${item.key}_${opt.key}`] = [opt.enabled !== false];
        });
      }
    });

    // Setup controls for non-static items
    this.nonStaticItems.forEach((item) => {
      controlsConfig[`enabled_${item.key}`] = [item.enabled === true];
      (item.staticBaseOptions || []).forEach((opt) => {
        controlsConfig[`enabled_${item.key}_static_${this.getStaticKey(opt)}`] = [true];
      });
    });

    this.form = this.formBuilder.group(controlsConfig);
  }

  async setLanguage(itemKey: string, lang: string) {
    // 4.2: static items are language-switchable too now — search both lists.
    const itemRef =
      this.nonStaticItems.find((i) => i.key === itemKey) ||
      this.items.find((i) => i.key === itemKey);
    if (itemRef) {
      // Enforce English-first: must have English label and at least one English option
      const labels = itemRef.label as { [key: string]: string };
      // Check dynamic options for En content
      const enLabelOk = !!(labels && labels["en"] && labels["en"].trim());
      // Check if any option has EN text? Static items carry no editable
      // option set, so the option requirement applies only to dynamic ones.
      const enHasOption = itemRef.static === true ||
                          (itemRef.staticBaseOptions && itemRef.staticBaseOptions.length > 0) ||
                          (itemRef.dynamicOptions && itemRef.dynamicOptions.some(o => !!(o.translations['en'] && o.translations['en'].trim())));
      
      if (lang !== "en" && (!enLabelOk || !enHasOption)) {
        this.snackBar.open("Add English label and options first", "OK", { duration: 2000 });
        this.selectedLang[itemKey] = "en";
        return;
      }
    }
    this.selectedLang[itemKey] = lang;
    if (itemRef) {
      await this.translateAllForItem(itemRef, lang);
    }
  }

  getLabel(item: RegistrationFormItem): string {
    const lang = this.selectedLang[item.key] || 'en';
    const labels = item.label as { [key: string]: string };
    return (labels && labels[lang]) || "";
  }

  setLabel(item: RegistrationFormItem, value: string) {
    const lang = this.selectedLang[item.key] || 'en';
    item.label = item.label || {};
    (item.label as any)[lang] = value;
    this.validateItem(item);
  }

  getOptions(item: RegistrationFormItem): string[] {
    // This method was used for iteration in template but we now iterate dynamicOptions directly or via helper
    // The template calls getOptions(item) in ngFor
    // We should return array of strings for current lang from dynamicOptions
    const lang = this.selectedLang[item.key] || "en";
    return (item.dynamicOptions || []).map(o => o.translations[lang] || "");
  }

  getDynamicOptions(item: RegistrationFormItem): { translations: { [lang: string]: string }, enabled: boolean }[] {
    return item.dynamicOptions || [];
  }

  async addOption(item: RegistrationFormItem, inputEl: HTMLInputElement) {
    const value = (inputEl && inputEl.value || "").trim();
    if (!value) return;
    
    const currentLang = this.selectedLang[item.key] || 'en';
    // Enforce English-first for adding options
    if (currentLang !== 'en') {
      this.snackBar.open('Add options in English first', 'OK', { duration: 2000 });
      return;
    }
    
    // Create new dynamic option
    const newOpt = { translations: { en: value }, enabled: true };
    this.languages.forEach(l => {
        if (l !== 'en') newOpt.translations[l] = "";
    });
    
    if (!item.dynamicOptions) item.dynamicOptions = [];
    item.dynamicOptions.push(newOpt);

    inputEl.value = "";
    this.validateItem(item);
  }

  removeOption(item: RegistrationFormItem, index: number) {
    if (item.dynamicOptions && item.dynamicOptions[index]) {
        item.dynamicOptions.splice(index, 1);
    }
    this.validateItem(item);
  }
  
  toggleOptionEnabled(item: RegistrationFormItem, index: number) {
      // Dynamic options do not have enable/disable; only static options have toggles
  }

  getStaticBaseOptions(item: RegistrationFormItem): string[] {
    return Array.isArray(item.staticBaseOptions) ? item.staticBaseOptions : [];
  }

  getStaticKey(enText: string): string {
    return String(enText || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  }

  getStaticLabel(item: RegistrationFormItem, enText: string): string {
    // Currently static options are not translated in the UI, they are just base identifiers
    return enText;
  }

  private async translateAllForItem(item: RegistrationFormItem, target: string) {
    if (!item) return;
    
    // Label Translation
    const labels = item.label as { [key: string]: string };
    const sourceLabel = (labels && labels["en"]) || this.getLabel(item);
    
    if (sourceLabel && target !== "en") {
      try {
        if (!labels[target]) {
           const tLabel = await this.translationService.translateText(sourceLabel, target);
           labels[target] = tLabel;
        }
      } catch (_) {}
    }
    
    // Dynamic Options Translation (use dynamicOptions structure)
    const dynOpts = item.dynamicOptions || [];
    if (dynOpts.length && target !== "en") {
        const indicesToTranslate: number[] = [];
        const textsToTranslate: string[] = [];
        
        dynOpts.forEach((opt, idx) => {
            const enVal = opt.translations['en'];
            const tVal = opt.translations[target];
            if (enVal && enVal.trim() && (!tVal || !tVal.trim())) {
                indicesToTranslate.push(idx);
                textsToTranslate.push(enVal);
            }
        });
        
        if (textsToTranslate.length) {
            try {
                const out = await this.translationService.translateBatch(textsToTranslate, target);
                out.forEach((trans, i) => {
                    const idx = indicesToTranslate[i];
                    if (dynOpts[idx]) dynOpts[idx].translations[target] = trans;
                });
            } catch (_) {}
        }
    }


    // Static Base Options Translation
    const baseOptions = this.getStaticBaseOptions(item);
    if (baseOptions.length && target !== 'en') {
      item.staticOptionsTranslations = item.staticOptionsTranslations || {};
      item.staticOptionsTranslations[target] = item.staticOptionsTranslations[target] || {};
      
      const toTranslate = baseOptions.filter(opt => !item.staticOptionsTranslations[target][opt]);
      
      if (toTranslate.length) {
        try {
          const translations = await this.translationService.translateBatch(toTranslate, target);
          toTranslate.forEach((opt, index) => {
             item.staticOptionsTranslations[target][opt] = translations[index] || opt;
          });
        } catch (_) {}
      }
    }
  }

  private updateNonStaticItemsForSave() {
    const updated = this.nonStaticItems
      .map((item) => {
        const enabledControl = this.form.get("enabled_" + item.key);
        const enabled = enabledControl ? !!enabledControl.value : !!item.enabled;
        
        const updatedItem: any = {
          key: item.key,
          type: item.type,
          label: item.label,
          required: item.required,
          static: item.static,
          enabled: enabled
        };

        if (!enabled) {
          updatedItem.options = {};
          return updatedItem;
        }

        // Build options per language combining enabled static base options + dynamic
        const base = this.getStaticBaseOptions(item);
        // ... (rest is same logic, assuming dynamicOptions is correct) ...
        const enabledStatic: string[] = base.filter((enText) => {
          const ctrl = this.form.get("enabled_" + item.key + "_static_" + this.getStaticKey(enText));
          return ctrl ? !!ctrl.value : true;
        });

        const outOptions: any = {};

        this.languages.forEach((lang) => {
          // Get translated static options for this language
          const staticTranslated = enabledStatic.map(enOpt => {
             if (lang === 'en') return enOpt;
             const transMap = item.staticOptionsTranslations && item.staticOptionsTranslations[lang];
             return (transMap && transMap[enOpt]) || enOpt;
          });
          
          // Append dynamic options
          const dyn = (item.dynamicOptions || []).map(opt => opt.translations[lang] || "");
          
          outOptions[lang] = staticTranslated.concat(dyn);
        });
        
        updatedItem.options = outOptions;
        return updatedItem;
      })
      ;

    // Remove editor-only fields from result
    updated.forEach((it: any) => {
      delete it.staticBaseOptions;
      delete it.staticOptionsTranslations;
      delete it.dynamicOptions;
    });
 
    return updated;
  }

  toggleManualEdit(key: string) {
    this.manualEditVisible[key] = !this.manualEditVisible[key];
  }

  getStaticOptionTranslation(item: RegistrationFormItem, baseOption: string): string {
    const lang = this.selectedLang[item.key];
    if (lang === 'en') return baseOption;
    
    const transMap = item.staticOptionsTranslations && item.staticOptionsTranslations[lang];
    return (transMap && transMap[baseOption]) || "";
  }

  setStaticOptionTranslation(item: RegistrationFormItem, baseOption: string, value: string) {
    const lang = this.selectedLang[item.key];
    if (lang === 'en') return;

    item.staticOptionsTranslations = item.staticOptionsTranslations || {};
    item.staticOptionsTranslations[lang] = item.staticOptionsTranslations[lang] || {};
    item.staticOptionsTranslations[lang][baseOption] = value;
    this.validateItem(item);
  }

  getDynamicOptionsEn(item: RegistrationFormItem): string[] {
      // Return English text from dynamicOptions
     return (item.dynamicOptions || []).map(o => o.translations['en'] || "");
  }

  getDynamicOptionTranslation(item: RegistrationFormItem, index: number): string {
    const lang = this.selectedLang[item.key] || 'en';
    const opt = item.dynamicOptions && item.dynamicOptions[index];
    return (opt && opt.translations[lang]) || "";
  }

  setDynamicTranslation(item: RegistrationFormItem, index: number, value: string) {
    const lang = this.selectedLang[item.key] || 'en';
    
    if (lang !== 'en') {
        // ... (validation or logic) ...
    }
    
    if (!item.dynamicOptions || !item.dynamicOptions[index]) return;
    item.dynamicOptions[index].translations[lang] = (value || "").trim();
    this.validateItem(item);
  }

  // Helper to get translated label for dynamic option in list
  getDynamicOptionLabel(item: RegistrationFormItem, index: number): string {
      const lang = this.selectedLang[item.key] || 'en';
      const opt = item.dynamicOptions && item.dynamicOptions[index];
      return (opt && opt.translations[lang]) || "";
  }

  // Robust Validation Logic
  private validateItem(item: RegistrationFormItem): boolean {
    const key = item.key;
    delete this.optionErrors[key]; // Clear previous error

    // 1. Check if Enabled
    // Use safe lookup for control
    const enabledControl = this.form && this.form.get("enabled_" + item.key);
    // If control exists, use its value. If not (e.g. strict static?), fallback to item.enabled
    const isEnabled = enabledControl ? !!enabledControl.value : !!item.enabled;
    
    if (!isEnabled) {
        return true; // Disabled items are always valid
    }

    // 2. Label Validation
    if (item.static) {
         // Static items typically have string labels. Validation is just sanity check.
         const labelStr = (typeof item.label === 'string') ? item.label : (item.label as any)['en'];
         if (!labelStr || !labelStr.trim()) {
              this.optionErrors[key] = "Enter a label";
          return false;
     }
} else {
     // Non-static (Dynamic) items have { en: "..." } structure
     const labels = item.label as { [key: string]: string };
     // We ONLY require English label
     if (!labels || !labels['en'] || !labels['en'].trim()) {
       this.optionErrors[key] = "Enter an English label";
           return false;
         }
    }

    // 3. Option Validation (Dropdowns & Radios)
    if (item.type === 'dropdown' || item.type === 'radio') {
       let allOptions: string[] = [];

       if (item.type === 'radio') {
           // Static Radio Options
           const opts = (item.options as RegistrationFormOption[]) || [];
           // For radios, we check if at least one sub-option is enabled
           const enabledOpts = opts.filter(opt => {
               const ctrl = this.form.get("enabled_" + item.key + "_" + opt.key);
               return ctrl ? !!ctrl.value : !!opt.enabled;
           });
           
           if (enabledOpts.length === 0) {
               this.optionErrors[key] = "Enable at least one option";
               return false;
           }
           return true; 
       }

       // Dropdown Validation
       if (item.type === 'dropdown') {
           // A. Static Base Options
           const base = this.getStaticBaseOptions(item);
           const enabledStatic = base.filter((enText) => {
              const ctrl = this.form && this.form.get("enabled_" + item.key + "_static_" + this.getStaticKey(enText));
              return ctrl ? !!ctrl.value : true;
           });
           allOptions = [...enabledStatic];

           // B. Dynamic Options
           const dynOpts = item.dynamicOptions || [];
           for(let i=0; i<dynOpts.length; i++) {
               const enVal = dynOpts[i].translations['en'];
               // Check for empty English values in dynamic rows
               if (!enVal || !enVal.trim()) {
                   this.optionErrors[key] = `Enter English text for option #${i+1}`;
                   return false;
               }
               allOptions.push(enVal.trim());
           }

           // C. Minimum Options Check
           if (allOptions.length === 0) {
               this.optionErrors[key] = "Add or enable at least one option";
               return false;
           }

           // D. Duplicate Check (Case-insensitive)
           const seen = new Set();
           for (const opt of allOptions) {
               const lower = opt.toLowerCase();
               if (seen.has(lower)) {
                   this.optionErrors[key] = `Remove or rename duplicate option: "${opt}"`;
                   return false;
               }
               seen.add(lower);
           }
       }
    }
    return true;
  }

  save() {
    this.optionErrors = {};
    let isValid = true;

    // Validate ALL items (Static + Non-Static)
    // We combine them to ensure everything enabled is validated
    const allItems = [...this.items, ...this.nonStaticItems];
    
    for (const item of allItems) {
        if (!this.validateItem(item)) {
            isValid = false;
        }
    }

    const emailItem = allItems.find(i => i.key === 'email');
    const confirmEmailItem = allItems.find(i => i.key === 'confirmEmail');
    
    if (emailItem && confirmEmailItem) {
        const emailControl = this.form.get("enabled_" + emailItem.key);
        const isEmailEnabled = emailControl ? !!emailControl.value : !!emailItem.enabled;
        
        const confirmEmailControl = this.form.get("enabled_" + confirmEmailItem.key);
        const isConfirmEmailEnabled = confirmEmailControl ? !!confirmEmailControl.value : !!confirmEmailItem.enabled;

        if (isConfirmEmailEnabled && !isEmailEnabled) {
            this.optionErrors['confirmEmail'] = "Enable the Email field to use Confirm Email";
            isValid = false;
        }
    }

    if (!isValid) {
      this.snackBar.open("Please fix validation errors before saving", "OK", { duration: 3000 });
      return;
    }

    // Process static items update
    const updatedStatics = this.items.map((item) => {
      const enabledControl = this.form.get("enabled_" + item.key);
      const requiredControl = this.form.get("required_" + item.key);

      const updatedItem: RegistrationFormItem = {
        ...item,
        enabled: enabledControl ? enabledControl.value : false,
        required: requiredControl ? requiredControl.value : false,
      };

      if (item.type === "radio" && Array.isArray(item.options)) {
        updatedItem.options = (item.options as RegistrationFormOption[]).map((opt) => {
            const optEnabledControl = this.form.get("enabled_" + item.key + "_" + opt.key);
            return {
              ...opt,
              enabled: optEnabledControl ? optEnabledControl.value : false,
            };
          }
        );
      }

      return updatedItem;
    });

    const updatedNonStatics = this.updateNonStaticItemsForSave();
    const updatedByKey: { [k: string]: RegistrationFormItem } = {};
    updatedStatics.forEach(u => { updatedByKey[u.key] = u as any; });
    (updatedNonStatics as any[]).forEach(u => { updatedByKey[u.key] = u as any; });
    const orderedResult = this.originalRegistrationForm.map(orig => {
      const updated = updatedByKey[orig.key];
      if (!updated) return orig as any;
      const out: any = {};
      out.key = updated.key;
      out.type = updated.type;
      out.label = updated.label;
      out.required = updated.required;
      out.static = updated.static;
      out.enabled = updated.enabled;
      if (Array.isArray((updated as any).options) || typeof (updated as any).options === 'object') {
        out.options = updated.options;
      }
      return out as RegistrationFormItem;
    });
    const filteredResult = orderedResult.filter((it: any) => !!it.enabled);
    // Unknown fields are never surfaced in this dialog's UI (no enabled toggle,
    // no validation), so they are not subject to the enabled filter above --
    // they are passed through exactly as received.
    const result = {
      registrationForm: [...filteredResult, ...this.unknownItems],
    };

    this.close(result);
  }
}
